import { MultiSelect } from 'primereact/multiselect'
import { FC, useEffect, useRef, useState } from 'react'

export type MetricOption = { label: string; value: string }

type MetricMultiSelectProps = {
  value: string[]
  options: MetricOption[]
  /** Fired once the user pauses, so rapid toggles trigger a single reload. */
  onChange: (values: string[]) => void
  className?: string
  /** Collapsed placeholder; shorten it where horizontal space is tight. */
  placeholder?: string
  /** Trims label padding and the chevron to reclaim horizontal space. */
  compact?: boolean
  appendTo?: 'self'
  tooltip?: string
}

// Long enough to batch a burst of checkbox clicks, short enough to still feel live.
const COMMIT_DELAY_MS = 1500

const sameSelection = (a: string[], b: string[]) =>
  a.length === b.length && a.every(v => b.includes(v))

/** Metric filter shared by the galleries, player, report builder and GIF modal. */
const MetricMultiSelect: FC<MetricMultiSelectProps> = ({
  value,
  options,
  onChange,
  className,
  placeholder = 'All Metrics',
  compact = false,
  appendTo,
  tooltip = 'Filter images by one or more metrics'
}) => {
  // Holds the in-progress selection until it settles.
  const [draft, setDraft] = useState<string[]>(value)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Kept in a ref so a pending timer always commits the latest selection.
  const commitRef = useRef<() => void>(() => undefined)
  commitRef.current = () => {
    if (!sameSelection(draft, value)) onChange(draft)
  }

  useEffect(() => {
    setDraft(value)
  }, [value])

  const clearTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = null
  }

  useEffect(() => clearTimer, [])

  return (
    <MultiSelect
      value={draft}
      options={options}
      optionLabel="label"
      optionValue="value"
      onChange={e => {
        setDraft(e.value ?? [])
        clearTimer()
        timerRef.current = setTimeout(() => commitRef.current(), COMMIT_DELAY_MS)
      }}
      onHide={() => {
        clearTimer()
        commitRef.current()
      }}
      placeholder={placeholder}
      maxSelectedLabels={1}
      selectedItemsLabel="{0} metrics"
      showSelectAll
      filter
      appendTo={appendTo}
      className={className}
      pt={{
        // Dropdown labels carry p-inputtext sizing; match it so neighbouring controls line up.
        label: { className: compact ? 'text-base !py-2 !pl-2 !pr-1 min-w-0' : 'text-base' },
        // Compact mode shrinks the chevron trigger to reclaim width.
        trigger: compact ? { className: '!w-6' } : undefined,
        // Repurpose the panel's close icon as a "clear selection" action.
        closeButton: {
          'aria-label': 'Clear metric selection',
          title: 'Clear selection',
          onClick: (event: MouseEvent) => {
            event.stopPropagation()
            clearTimer()
            setDraft([])
            if (value.length) onChange([])
          }
        }
      }}
      tooltip={tooltip}
      tooltipOptions={{ position: 'top' }}
    />
  )
}

export default MetricMultiSelect
